import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { map, switchMap, withLatestFrom, debounceTime, tap } from 'rxjs/operators';
import { InvoicingService } from 'apps/debtor-portal/src/app/accounting-module/services';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from '@aston/foundation';
import { saveAs } from 'file-saver';
import { DocumentFilesViewerComponent } from 'apps/debtor-portal/src/app/shared-module/components/document-files-viewer/document-files-viewer.component';

import { CommentsStoreActions } from '../../comments-store';
import { catchWithAppError as catchError } from '../../app-store';
import * as appActions from '../../app-store/actions';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';


@Injectable({providedIn: 'root'})
export class AccountingStoreInvoicingEffects {
	private actions$ = inject(Actions);
	private store = inject(Store);
	protected modalService = inject(NgbModal);
	protected translateService = inject(TranslateService);
	private invoicingService = inject(InvoicingService);


	loadInvoicesEffect$ = createEffect(() => this.actions$.pipe(
		ofType(
			featureActions.LoadInvoicingRequest,
			CommentsStoreActions.DiscussModalClose,
		),
		withLatestFrom(this.store.select(featureSelectors.selectState)),
		debounceTime(500),
		switchMap(([_, state]) => {
			const { paging } = state.invoicesList;
			return this.invoicingService.getInvoices({ ...paging }).pipe(
				map(list => featureActions.LoadInvoicingSuccess({ list })),
				catchError(error => of(featureActions.LoadInvoicingFailure({ error })))
			);
		})
	));

	pagingEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateInvoicingSettings),
		map(_ => featureActions.LoadInvoicingRequest())
	));

	onOpenInvoice$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenInvoice),
		tap(action => {
			const modalRef = this.modalService.open(DocumentFilesViewerComponent, AppConstants.DEFAULT_MODAL_OPTIONS);
			modalRef.componentInstance.item = { id: action.invoiceId };
			modalRef.componentInstance.title = this.translateService.instant('PageTitles.InvoicingDetail', { data: { id: action.invoiceNumber } });
		})
	), { dispatch: false });

	loadSuccessEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadInvoicingSuccess),
		map(_ => appActions.StartScrollToTop())
	));

	exportInvoicesEffect$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.ExportInvoicingRequest),
		withLatestFrom(this.store.select(featureSelectors.selectState)),
		switchMap(([action, state]) => {
			const { paging } = state.invoicesList;
			return this.invoicingService.exportInvoicingList({ ...paging }).pipe(
				map(response => {
					saveAs(response.blob, response.fileName);
					return featureActions.ExportInvoicingSuccess({ correlationParams: action.correlationParams });
				}),
				catchError(error => of(
					featureActions.ExportInvoicingFailure({ error }),
					appActions.ToastError('Errors.RetryableError')
				))
			);
		})
	));
}
